// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE SUBMITTED FILE'S PAGE (LO plan v7, Jesse 2026-10-28): /doorlypro/submissions/:id.
//
// The same sections and rows as Review and submit, read-only (no Edit); "Submitted <date, time>" under the name and the
// DoorlyPro status in the app's status pill beside it. No calculated card: each income source shows its figure at
// submit, Documents shows "Verified assets at submit", and one note at the bottom — all from the snapshot frozen at
// submit (GET /api/doorlypro/submissions/:id). Labels sit above values on a phone; nothing scrolls sideways.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import LoReviewRows from '../../components/doorlypro/submission/LoReviewRows';
import '../../components/doorlypro/submission/loSubmission.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const when = (d) => {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const h = x.getHours(); const m = String(x.getMinutes()).padStart(2, '0');
  return `${MON[x.getMonth()]} ${x.getDate()}, ${x.getFullYear()}, ${((h + 11) % 12) + 1}:${m} ${h < 12 ? 'AM' : 'PM'}`;
};
const day = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? '' : `${MON[x.getMonth()]} ${x.getDate()}, ${x.getFullYear()}`; };
// The app's status pill, in the semantic colours (constants/statusColors): slate while it waits, cyan once the
// borrower has it, red when it lapsed.
const PILL = {
  waiting_on_borrower: { bg: '#64748B', fg: '#ffffff' },
  with_borrower: { bg: '#0EA5E9', fg: '#1a1a1a' },
  expired: { bg: '#fff0f3', fg: '#ff082f' },
  withdrawn: { bg: '#f3f4f6', fg: '#6b7280' },
};

// 🔑 BEHIND THE SIGN-IN CHECK, LIKE EVERY OTHER DoorlyPro PAGE (broker report 1102, Jesse 2026-11-02). This page was
// routed bare: a signed-out visitor got the partner shell and its requests went out with a null token (each refused by
// the server — verifyPartnerToken — but the page itself never sent them to sign in). RequirePartnerAuth holds the render
// until the session is read and sends a signed-out visitor to /doorlypro/login; the page's own hooks run only after.
export default function DoorlyProSubmissionFile() {
  return (
    <RequirePartnerAuth>
      <DoorlyProSubmissionFilePage />
    </RequirePartnerAuth>
  );
}

function DoorlyProSubmissionFilePage() {
  const { id } = useParams();
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('doorlypro_token');
    fetch(`${API_URL}/api/doorlypro/submissions/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Not found'); setFile(d); })
      .catch(() => setError('This file could not be opened.'));
  }, [id]);
  const pill = file ? (PILL[file.state] || PILL.waiting_on_borrower) : null;
  return (
    <DoorlyProLayout>
      <div data-testid="lo-file-page" style={{ maxWidth: 880, margin: '0 auto', padding: '24px 16px 80px' }}>
        <Link to="/doorlypro/submissions?tab=submitted" style={{ fontSize: 14, fontWeight: 600, color: '#3a6ad9', textDecoration: 'none' }}>← Submitted</Link>
        {error && <p style={{ marginTop: 20, color: '#b3321f' }}>{error}</p>}
        {!file && !error && <p style={{ marginTop: 20, color: '#6b7280' }}>Loading…</p>}
        {file && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
              <h1 data-testid="lo-file-name" style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>{file.borrower}</h1>
              <span className="lo-pill" data-testid="lo-file-status" style={{ background: pill.bg, color: pill.fg }}>
                {file.status_label}{file.expires_at ? ` · expires ${day(file.expires_at)}` : ''}
              </span>
            </div>
            <div data-testid="lo-file-submitted" style={{ fontSize: 13.5, color: '#374151', margin: '6px 0 20px' }}>Submitted {when(file.submitted_at)}</div>
            {file.review ? <LoReviewRows sections={file.review} />
              : <p style={{ fontSize: 14, color: '#6b7280' }}>This file was sent before submissions had their own page, so only its status is shown.</p>}
            <p data-testid="lo-file-note" style={{ fontSize: 12.5, color: '#6b7280', marginTop: 16 }}>{file.note}</p>
          </>
        )}
      </div>
    </DoorlyProLayout>
  );
}
